/**
 * `#repscreen` — the storage-location and non-mover review reports.
 *
 * A 1:1 port of the prototype's `openReport` / `renderRepTable`: the DOM structure, class
 * names and inline styles are kept so `proto.css` applies unchanged. Both report ids are
 * served off the `:reportId` route param; the row set and all copy come from `@/lib/data`.
 */

import { type CSSProperties, useState } from "react";
import { Navigate, useParams } from "react-router";

import {
  REPORT_META,
  type ReportId,
  type ReportRow,
  useDataLabels,
  useReportRows,
} from "@/lib/data";

/** Rows rendered at a time. The prototype stopped at 80 and said so; a real drop has 8,771, and
 *  a review you cannot scroll past row 80 is a sample wearing a report's title. */
const PAGE = 80;

/**
 * The whole report as CSV — every row, not the rendered page, with each row's decision.
 *
 * The point of the screen is a hand-off: a slotting list the WMS team can check against their own
 * data, a non-mover list Planning can work. Until this existed the button said "Demo: would export
 * the report", which made the deliverable the one thing the tool could not produce.
 */
function toCsv(cols: readonly string[], rows: readonly ReportRow[], removed: ReadonlySet<number>) {
  // Excel-safe quoting: wrap every field, double any embedded quote. The cells are pre-rendered
  // strings and carry commas ("1,327 boxes") and en-dashes, so nothing here may be naive.
  const cell = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const head = [...cols, "Decision"].map(cell).join(",");
  const body = rows.map((r) =>
    [...r.cells, removed.has(r.skuId) ? "Removed" : "Accepted"].map(cell).join(","),
  );
  return [head, ...body].join("\r\n");
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  URL.revokeObjectURL(url);
}

function toReportId(id: string | undefined): ReportId | null {
  return id === "storage" || id === "nonmover" ? id : null;
}

/** First cell is the SKU name, last is the "why" prose; the rest are plain. */
function cellClass(index: number, count: number): string | undefined {
  if (index === count - 1) {
    return "rs";
  }
  return index === 0 ? "nm" : undefined;
}

export function ReportScreen() {
  const { reportId } = useParams();
  const id = toReportId(reportId);

  if (!id) {
    return <Navigate to="/scenarios" replace />;
  }
  // Keyed so switching reports resets the accepted set, as `openReport` did.
  return <Report key={id} reportId={id} />;
}

function Report({ reportId }: { reportId: ReportId }) {
  const { data: rows } = useReportRows(reportId);
  const { data: labels } = useDataLabels();
  const [removed, setRemoved] = useState<ReadonlySet<number>>(new Set());
  const [shown, setShown] = useState(PAGE);

  const meta = REPORT_META[reportId];
  const total = rows?.length ?? 0;

  // Opening a report deliberately does NOT touch the field's selection.
  //
  // The prototype's `openReport` selected the report's whole cohort, so "Back to SKU view" read the
  // report on the plot. That made sense when a report could only be reached from the agent — but
  // these are sidebar destinations now, and merely LOOKING at the non-mover list silently replaced
  // whatever the user had marqueed with 9,545 SKUs. The agent path still highlights its cohort: it
  // applies the selection itself before offering the link, so nothing was lost by dropping this.
  function toggle(skuId: number) {
    setRemoved((prev) => {
      const next = new Set(prev);
      if (!next.delete(skuId)) {
        next.add(skuId);
      }
      return next;
    });
  }

  return (
    <div className="capscreen on" id="repscreen">
      <div className="cap-top">
        <div style={{ flex: 1 }} />
        <span className="demo-badge" style={{ marginRight: 12 }} title={labels?.caption}>
          {labels?.badge ?? ""}
        </span>
        <button
          className="btn-primary"
          id="rep-export"
          disabled={!rows?.length}
          onClick={() =>
            rows && download(`${reportId}-review.csv`, toCsv(meta.cols, rows, removed))
          }
        >
          Export all {total.toLocaleString()} rows (CSV)
        </button>
      </div>
      {rows && (
        <div className="cap-wrap">
          <div className="cap-hero" id="rep-narr" style={{ marginBottom: 20 }}>
            {/* Provider-authored copy with bolded counts — the same HTML the prototype injected. */}
            <div
              style={{ fontSize: 13, lineHeight: 1.6, color: "var(--w8)" }}
              dangerouslySetInnerHTML={{ __html: meta.narrative(rows.length) }}
            />
            {/* The badge in the header is hover-only, and this page is downloadable — so the
                provenance of the rows is stated on the page itself, not in a tooltip. */}
            {labels?.caption ? (
              <div style={{ fontSize: 11, lineHeight: 1.5, color: "var(--w6)", marginTop: 10 }}>
                {labels.caption}
              </div>
            ) : null}
          </div>
          <div id="rep-table">
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: 12,
              }}
            >
              <h3 style={{ fontSize: 15 }}>Recommendations</h3>
              {/* What is actually true: how much of the report has been LOOKED at, and how many
                  rows were struck off. It used to read "8,691 of 8,771 accepted" — counting 8,691
                  rows nobody could scroll to as reviewed and accepted. */}
              <span style={{ fontSize: 12, color: "var(--w7)" }} id="rep-count">
                Showing <b>{Math.min(shown, total).toLocaleString()}</b> of {total.toLocaleString()}
                {removed.size ? (
                  <>
                    {" · "}
                    <b>{removed.size.toLocaleString()}</b> removed
                  </>
                ) : null}
              </span>
            </div>
            {/* One inline custom property, not a utility class: the track list depends on the
                report's column count, which Tailwind cannot express. SKU widest, "why" wide, the
                Action column fixed, everything between them even. */}
            <div
              className="rep-tbl"
              style={
                {
                  "--rep-cols": `1.7fr ${"0.9fr ".repeat(Math.max(0, meta.cols.length - 2))}2fr 0.95fr`,
                } as CSSProperties
              }
            >
              <div className="rep-tr h">
                {meta.cols.map((col) => (
                  <span key={col}>{col}</span>
                ))}
                <span style={{ textAlign: "right" }}>Action</span>
              </div>
              {rows.slice(0, shown).map((row) => {
                const rm = removed.has(row.skuId);
                return (
                  <div key={row.skuId} className={rm ? "rep-tr off" : "rep-tr"}>
                    {row.cells.map((cell, i) => (
                      <span key={meta.cols[i] ?? i} className={cellClass(i, row.cells.length)}>
                        {cell}
                      </span>
                    ))}
                    <span style={{ textAlign: "right" }}>
                      <button
                        className={rm ? "ar-btn" : "ar-btn on"}
                        data-rep={row.skuId}
                        onClick={() => toggle(row.skuId)}
                      >
                        {rm ? "Removed" : "Accepted"}
                      </button>
                    </span>
                  </div>
                );
              })}
            </div>
            {shown < total && (
              <div className="rep-more">
                <button id="rep-more" onClick={() => setShown((n) => n + PAGE * 5)}>
                  Show {Math.min(PAGE * 5, total - shown).toLocaleString()} more
                </button>
                <button className="ghost" onClick={() => setShown(total)}>
                  Show all {total.toLocaleString()}
                </button>
                <span>ranked, so the rows that matter most are already above</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
